import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink, Check, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { showError } from "@/lib/errors";

const CATEGORIES = [
  { v: "actualite", label: "Actualité" },
  { v: "resultats", label: "Résultats" },
  { v: "portrait", label: "Portrait" },
  { v: "interview", label: "Interview" },
  { v: "competition_a_venir", label: "Compétition à venir" },
  { v: "retour_competition", label: "Retour compétition" },
  { v: "communique_club", label: "Communiqué club" },
  { v: "communique_federation", label: "Communiqué fédération" },
  { v: "billet_humeur", label: "Billet d'humeur" },
  { v: "video", label: "Vidéo" },
  { v: "galerie", label: "Galerie" },
  { v: "autre", label: "Autre" },
];

/**
 * Page de curation rapide alimentée par les paramètres URL du bookmarklet :
 *   /quick-curate?url=...&title=...&image=...&description=...&source=...
 *
 * - Préremplit le formulaire automatiquement
 * - Relance une extraction serveur (/api/curator/extract) pour enrichir/vérifier
 * - Soumet en `proposed` (contributeur) ou `published` (admin/copf/contributor_trusted)
 */
export default function QuickCuratePage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);

  const [form, setForm] = useState({
    url: qs.get("url") || "",
    title: qs.get("title") || "",
    image_url: qs.get("image") || "",
    description: qs.get("description") || "",
    summary: "",
    source_name: qs.get("source") || "",
    category: "actualite",
    discipline: "",
    rights_declaration: false,
  });
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [success, setSuccess] = useState(null);

  // V7 : Quick Curate envoie TOUJOURS en validation admin, peu importe le rôle.
  // La publication finale et les rattachements se font côté admin.
  const canSubmit = !!user && (user.roles || []).some((r) => ["admin", "copf", "contributor", "contributor_trusted"].includes(r));

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/connexion?next=${encodeURIComponent(loc.pathname + loc.search)}`); return; }
    if (!canSubmit) { toast.error("Réservé aux contributeurs et administrateurs."); navigate("/"); return; }
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {});
    // Auto-extract si une URL est fournie mais titre/image manquants
    if (form.url && (!form.title || !form.image_url)) {
      runExtract();
    }
    // eslint-disable-next-line
  }, [authLoading, user]);

  const runExtract = async () => {
    if (!form.url) { toast.error("URL requise."); return; }
    setExtracting(true);
    try {
      const { data } = await api.post("/curator/extract", { url: form.url });
      setForm((f) => ({
        ...f,
        title: f.title || data.title || "",
        image_url: f.image_url || data.image_url || "",
        description: f.description || data.description || "",
        summary: data.summary || f.summary || "",
        source_name: f.source_name || data.source_name || "",
        category: f.category === "actualite" ? (data.category || "actualite") : f.category,
      }));
      toast.success("Métadonnées extraites de la source.");
    } catch (e) { showError(e, { prefix: "Extraction :" }); }
    finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!form.url || !form.title) { toast.error("URL et titre obligatoires."); return; }
    if (!form.rights_declaration) { toast.error("Tu dois cocher la déclaration de droits."); return; }
    setSubmitting(true);
    try {
      const payload = {
        url: form.url,
        title: form.title,
        image_url: form.image_url || null,
        description: form.description || null,
        summary: form.summary || null,
        source_name: form.source_name || null,
        category: form.category,
        discipline: form.discipline || null,
        rights_declaration: true,
        submission_method: "bookmarklet",
      };
      const { data } = await api.post("/curator/items", payload);
      setSuccess({ id: data.id, status: data.status, isAdminPublished: false });
    } catch (e) { showError(e); }
    finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-12 text-center">
        <Card className="p-8">
          <Check className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="font-display text-2xl font-bold">Envoyé pour validation</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Un administrateur va relire, enrichir et rattacher ta proposition très bientôt.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <Button onClick={() => { setSuccess(null); setForm({ ...form, url: "", title: "", image_url: "", description: "", summary: "", rights_declaration: false }); }}>Ajouter un autre</Button>
            <Button variant="outline" onClick={() => navigate("/actualites")}>Voir les actualités</Button>
            <Button variant="outline" onClick={() => window.close()}>Fermer</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Curation rapide</h1>
        <Badge variant="outline" data-testid="qc-validation-badge">Envoi en validation admin</Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Outil simple de collecte de liens. Chaque envoi part en file de validation. La publication finale et les rattachements (athlètes, clubs, fédérations…) se font ensuite depuis l'interface admin Curator.
      </p>

      <Card className="p-5 space-y-4">
        <div>
          <Label className="text-xs">URL de l'article *</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="qc-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
            <Button onClick={runExtract} disabled={extracting || !form.url} variant="outline" data-testid="qc-extract">
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Titre *</Label>
            <Input data-testid="qc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Source / média</Label>
            <Input data-testid="qc-source" value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} placeholder="Tahiti Infos…" />
          </div>
        </div>

        <div>
          <Label className="text-xs">URL de l'image (vignette)</Label>
          <Input data-testid="qc-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          {form.image_url && <img src={form.image_url} alt="" className="mt-2 max-h-32 rounded border" />}
        </div>

        <div>
          <Label className="text-xs">Résumé / extrait</Label>
          <Textarea data-testid="qc-summary" value={form.summary || form.description} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Type de contenu</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="qc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Discipline</Label>
            <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
              <SelectTrigger data-testid="qc-discipline"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent className="max-h-60">{disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Déclaration de droits obligatoire */}
        <div className="flex items-start gap-3 p-3 rounded-md border bg-[hsl(var(--aito-gold)/0.06)]">
          <input
            id="qc-rights"
            data-testid="qc-rights"
            type="checkbox"
            checked={form.rights_declaration}
            onChange={(e) => setForm({ ...form, rights_declaration: e.target.checked })}
            className="h-4 w-4 mt-1 accent-[hsl(var(--aito-gold))] shrink-0"
          />
          <label htmlFor="qc-rights" className="text-xs leading-relaxed cursor-pointer">
            Je confirme disposer des droits, autorisations ou fondements nécessaires pour proposer cette publication sur AITO.PF.
          </label>
        </div>

        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || !form.rights_declaration || !form.url || !form.title} data-testid="qc-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
            Envoyer pour validation
          </Button>
        </div>
      </Card>
    </div>
  );
}
